import {
  set, observable, computed,
} from 'mobx';
import { DOOR_STATUS, IStoreInformation } from '../../../api/IApiResponses';
import { isRawTimeValid } from '../../../utils/TimeUtils';

export interface IStore {
  city: string;
  country: string;
  zipcode: string;
  address: string;
}

export interface IInventory {
  availableQuantity: number;
  isAvailable: number;
  skuBaseId: number;
  isSampleItem: number;
  isFreeItem: number;
  isGWPOfferItem: number;
  GWPSaleableSku: number;
}

export interface IOpeningHours {
  openingHours: number;
  closedHours: number;
  day: number;
}

export interface IPurchaserInfo {
  phone: string;
  email?: string;
  lastName: string;
  firstName: string;
}

export enum INVENTORY {
  ALL = 'ALL',
  PARTIAL = 'PARTIAL',
  NONE = 'NONE',
}

export enum UNIT {
  MILE = 'mile',
  KILOMETER = 'km',
}

class Store {
  @observable city: string;

  @observable country: string;

  @observable zipcode: string;

  @observable address: string;

  @observable distance: number;

  @observable name: string;

  @observable phoneNumber: string;

  @observable latitude: number;

  @observable longitude: number;

  @observable doorId: string;

  @observable state: string;

  @observable subHeading = '';

  @observable openingHours: IOpeningHours[] = [];

  @observable workingTime: string[] = [];

  @observable inventory: IInventory[] = [];

  @observable isCourierSelected?: boolean;

  @observable unitOfMeasure?: UNIT;

  @observable courierRadius?: number;

  @observable timeZone: string;

  @observable timeZoneOffsetSeconds: string;

  info: IStoreInformation;

  courierDelivery: DOOR_STATUS;

  static fromJSON(data: IStore): Store {
    const store = new Store();
    set(store, data);

    return store;
  }

  @computed
  get itemsAvailable(): INVENTORY {
    const inventory = this.inventory.filter(item => {
      const isNotFreeItem = !item.isSampleItem && !item.isFreeItem;
      const isGWPOfferItem = item.isGWPOfferItem;
      return isNotFreeItem || isGWPOfferItem;
    });
    let allAvailable = true;
    let noneAvailable = true;
    inventory.forEach(item => {
      if (noneAvailable && item.isAvailable) {
        noneAvailable = false;
      }
      if (!item.isAvailable) {
        allAvailable = false;
      }
    });

    if (noneAvailable) {
      return INVENTORY.NONE;
    }
    if (allAvailable) {
      return INVENTORY.ALL;
    }
    return INVENTORY.PARTIAL;
  }

  @computed
  get isAvailableForToday(): boolean {
    const currentTime = new Date();
    const hours = currentTime.getHours();
    const minutes = currentTime.getMinutes();
    const currentHoursMinutes = Number(`${hours}${minutes}`);
    const todaySchedule = this.openingHours
      && this.openingHours.find(x => x.day + 1 === currentTime.getDay());
    const findDIfference = todaySchedule && todaySchedule.closedHours - currentHoursMinutes;
    // Check if index out of bounds when there are no stores.
    const isStoreClosed = currentTime.getDay() <= this.openingHours?.length ? !this.openingHours?.[currentTime.getDay()]?.openingHours : false;
    if (!isStoreClosed
      && findDIfference > 199
      && todaySchedule.closedHours > currentHoursMinutes
      && this.itemsAvailable === INVENTORY.ALL
    ) {
      return true;
    }
    return false;
  }

  @computed
  get isCourierActive(): boolean {
    return this.courierDelivery === DOOR_STATUS.ACTIVE;
  }

  @computed
  get timeZoneOffsetHours(): number {
    return Number(this.timeZoneOffsetSeconds) / 60 / 60;
  }

  @computed
  get isStoreOpen(): boolean {
    const currentTime = new Date();
    return this.openingHours && Boolean(this.openingHours[currentTime.getDay()]?.openingHours);
  }

  public hasInvalidStoreHours(day: number): boolean {
    const storeHours = (this.openingHours || []).find(
      hours => hours.day === day);
    return !isRawTimeValid(storeHours?.openingHours) || !isRawTimeValid(storeHours?.closedHours);
  }
}

export default Store;
